/**
 * BootloaderTransferStep Component
 *
 * Step 3: Transfer bootloader to the device
 * This step uses the real IMXLoaderService to transfer the bootloader file.
 * After transfer, the device will reboot with new VID:PID for the next stage.
 */

import * as React from 'react';
import { Icon } from '../../../../shared/Icon';
import { useUpdaterServices } from '../../hooks/useUpdaterServices';
import { useWizard } from '../../hooks/useWizard';
import { wrapHIDDevice } from '../../types/imx-types';
import {
  ErrorCode,
  RecoveryAction,
  StateActionType,
  StepState,
  UpdaterError,
  WizardStep,
} from '../../types/wizard-types';
import {
  InfoSection,
  InfoText,
  PhaseIcon,
  PhaseItem,
  PhaseLabel,
  PhaseList,
  StepContainer,
} from '../StepStyles';
import { TransferProgress } from '../TransferProgress';
import { WarningBanner } from '../WarningBanner';

/**
 * Transfer phases for the bootloader
 */
type TransferPhase =
  | 'preparing'
  | 'connecting'
  | 'dcd'
  | 'bootloader'
  | 'jumping'
  | 'complete';

/**
 * BootloaderTransferStep component
 */
export const BootloaderTransferStep: React.FC = (): React.ReactElement => {
  const { state, setProgress, dispatch, setError, nextStep } = useWizard();
  const {
    hidDevice,
    imxLoader,
    logger,
    loadFile,
    setProgressCallback,
    setHidDevice,
    fetchFirmware,
    firmware,
  } = useUpdaterServices();

  const [phase, setPhase] = React.useState<TransferPhase>('preparing');
  const [hasStarted, setHasStarted] = React.useState(false);

  /**
   * Perform the actual bootloader transfer
   */
  const startBootloaderTransfer = React.useCallback(async (): Promise<void> => {
    if (hasStarted) return;
    setHasStarted(true);

    // Mark step as in progress
    dispatch({
      type: StateActionType.SET_STEP_STATE,
      step: WizardStep.BOOTLOADER_TRANSFER,
      state: StepState.IN_PROGRESS,
    });

    try {
      // Verify we have a device
      if (!hidDevice) {
        throw new Error(
          'No plate connected. Please go back and connect your plate.'
        );
      }

      // Phase 1: Preparing - fetch firmware metadata and signed URLs from GCS
      setPhase('preparing');
      setProgress(0, 'Preparing update...');
      logger.info('Starting bootloader transfer phase');

      // Fetch firmware metadata and signed URLs from GCS (if not already loaded)
      // This only fetches the manifest with URLs, actual files are fetched lazily by loadFile()
      if (!firmware) {
        logger.info('Fetching firmware metadata from cloud storage');
        const firmwareInfo = await fetchFirmware();
        logger.success(
          `Firmware v${firmwareInfo.version} ready (${
            Object.keys(firmwareInfo.files).length
          } files available)`
        );
      } else {
        logger.info(`Using cached firmware v${firmware.version}`);
      }

      // Set up progress callback - scale 0-100% from IMX loader to 0-90%
      // We reserve 90-100% for the reboot wait animation
      setProgressCallback((percent: number): void => {
        const scaledPercent = Math.round(percent * 0.9);
        setProgress(scaledPercent, 'Transferring update data...');
      });

      // Phase 2: Connecting to device via IMX loader
      setPhase('connecting');
      logger.info('Connecting to plate via IMX loader');

      // Connect to the device using IMX loader
      const wrappedDevice = wrapHIDDevice(hidDevice);
      const config = await imxLoader.connectDevice(wrappedDevice);
      logger.success(`Connected to plate: ${config.name}`);

      // Phase 3: DCD Transfer (if applicable)
      setPhase('dcd');

      // Phase 4: Bootloader Transfer
      setPhase('bootloader');
      logger.info('Starting file transfer using IMX loader');

      // Load images using the IMX loader service
      // This will use the config.work items to determine what files to load
      const success = await imxLoader.loadImages(loadFile);

      if (!success) {
        throw new Error('Update preparation failed');
      }

      // Phase 5: Jump to bootloader
      setPhase('jumping');
      logger.info('Plates are rebooting with new bootloader');

      // The device will disconnect and re-enumerate with new VID:PID
      // Close the device connection and forget it to release Chrome's permission cache
      try {
        // Close the device first (hidDevice guaranteed non-null by check at line 80)
        if (hidDevice.opened) {
          logger.info('Closing HID plate connection');
          await hidDevice.close();
        }

        // Forget the device to clear Chrome's permission cache
        // This helps ensure the new device (with different VID:PID) can be selected
        // Note: 'forget' is a Chrome-specific method not in standard HIDDevice types
        const deviceWithForget = hidDevice as HIDDevice & {
          forget?: () => Promise<void>;
        };
        if (typeof deviceWithForget.forget === 'function') {
          logger.info('Forgetting plate to clear permission cache');
          await deviceWithForget.forget();
        }
      } catch (closeErr) {
        // Device may already be disconnected, which is fine
        logger.debug(
          'Plate cleanup error (may already be disconnected)',
          closeErr
        );
      }

      // Clear the current HID device reference
      setHidDevice(null);

      // Animate progress from 90% to 99% during the 3-second reboot wait
      // This creates a smooth transition where 100% coincides with completion
      setProgress(90, 'Preparing plates...');
      const rebootWaitMs = 3000;
      const animationSteps = 9; // 90% -> 99% in 9 steps
      const stepDelayMs = rebootWaitMs / animationSteps;

      for (let i = 1; i <= animationSteps; i++) {
        await new Promise<void>((resolve: () => void): void => {
          setTimeout(resolve, stepDelayMs);
        });
        setProgress(90 + i, 'Preparing plates...');
      }

      // Complete - 100% and green happen together
      setPhase('complete');
      setProgress(100, 'Preparation complete!');
      logger.success('Bootloader transfer completed successfully');

      // Mark step as completed
      dispatch({
        type: StateActionType.SET_STEP_STATE,
        step: WizardStep.BOOTLOADER_TRANSFER,
        state: StepState.COMPLETED,
      });

      // Clear progress callback
      setProgressCallback(undefined);

      // Auto-advance after a short delay
      await new Promise<void>((resolve: () => void): void => {
        setTimeout(resolve, 1000);
      });
      nextStep();
    } catch (err: unknown) {
      logger.error('Bootloader transfer error', err);

      // Clear progress callback
      setProgressCallback(undefined);

      // If it's already an UpdaterError (e.g., from firmware service), use it directly
      if (err instanceof UpdaterError) {
        setError(err);
      } else {
        const message = err instanceof Error ? err.message : 'Unknown error';
        const stack = err instanceof Error ? err.stack : undefined;
        setError(
          new UpdaterError(
            `Update preparation failed: ${message}`,
            ErrorCode.BOOTLOADER_TRANSFER_FAILED,
            WizardStep.BOOTLOADER_TRANSFER,
            [
              {
                action: RecoveryAction.RETRY_STEP,
                label: 'Retry Update',
                description: 'Try preparing the update again',
                order: 1,
              },
              {
                action: RecoveryAction.POWER_CYCLE,
                label: 'Power Cycle Plates',
                description:
                  'Disconnect power, wait 10 seconds, reconnect and start over',
                order: 2,
              },
              {
                action: RecoveryAction.START_OVER,
                label: 'Start Over',
                description: 'Restart the entire update process',
                order: 3,
              },
            ],
            stack
          )
        );
      }
    }
  }, [
    hasStarted,
    hidDevice,
    imxLoader,
    logger,
    loadFile,
    setProgressCallback,
    setHidDevice,
    dispatch,
    setProgress,
    setError,
    nextStep,
    fetchFirmware,
    firmware,
  ]);

  // Start transfer when component mounts
  React.useEffect((): void => {
    startBootloaderTransfer();
  }, [startBootloaderTransfer]);

  /**
   * Get phase status icon
   */
  const getPhaseStatus = (
    targetPhase: TransferPhase
  ): 'pending' | 'active' | 'complete' => {
    const phases: TransferPhase[] = [
      'preparing',
      'connecting',
      'dcd',
      'bootloader',
      'jumping',
      'complete',
    ];
    const currentIndex = phases.indexOf(phase);
    const targetIndex = phases.indexOf(targetPhase);

    if (targetIndex < currentIndex) return 'complete';
    if (targetIndex === currentIndex) return 'active';
    return 'pending';
  };

  return (
    <StepContainer>
      {/* Warning Banner */}
      <WarningBanner
        title='Do not disconnect your plates!'
        message='Keep your plates connected until this step completes.'
      />

      {/* Progress Display */}
      <TransferProgress
        progress={state.currentProgress}
        message={state.currentProgressMessage}
        isActive={phase !== 'complete'}
        isComplete={phase === 'complete'}
      />

      {/* Phase Breakdown */}
      <PhaseList>
        <PhaseItem $status={getPhaseStatus('preparing')}>
          <PhaseIcon $status={getPhaseStatus('preparing')}>
            {getPhaseStatus('preparing') === 'complete' ? (
              <Icon name='check' color='white' size='tiny' />
            ) : (
              <Icon name='folder' size='tiny' />
            )}
          </PhaseIcon>
          <PhaseLabel>Prepare update files</PhaseLabel>
        </PhaseItem>

        <PhaseItem $status={getPhaseStatus('connecting')}>
          <PhaseIcon $status={getPhaseStatus('connecting')}>
            {getPhaseStatus('connecting') === 'complete' ? (
              <Icon name='check' color='white' size='tiny' />
            ) : (
              <Icon name='link' size='tiny' />
            )}
          </PhaseIcon>
          <PhaseLabel>Connect to plates</PhaseLabel>
        </PhaseItem>

        <PhaseItem $status={getPhaseStatus('dcd')}>
          <PhaseIcon $status={getPhaseStatus('dcd')}>
            {getPhaseStatus('dcd') === 'complete' ? (
              <Icon name='check' color='white' size='tiny' />
            ) : (
              <Icon name='settings' size='tiny' />
            )}
          </PhaseIcon>
          <PhaseLabel>Configure plates</PhaseLabel>
        </PhaseItem>

        <PhaseItem $status={getPhaseStatus('bootloader')}>
          <PhaseIcon $status={getPhaseStatus('bootloader')}>
            {getPhaseStatus('bootloader') === 'complete' ? (
              <Icon name='check' color='white' size='tiny' />
            ) : (
              <Icon name='memory' size='tiny' />
            )}
          </PhaseIcon>
          <PhaseLabel>Transfer update data</PhaseLabel>
        </PhaseItem>

        <PhaseItem $status={getPhaseStatus('jumping')}>
          <PhaseIcon $status={getPhaseStatus('jumping')}>
            {getPhaseStatus('jumping') === 'complete' ? (
              <Icon name='check' color='white' size='tiny' />
            ) : (
              <Icon name='play_arrow' size='tiny' />
            )}
          </PhaseIcon>
          <PhaseLabel>Prepare plates</PhaseLabel>
        </PhaseItem>

        <PhaseItem $status={getPhaseStatus('complete')}>
          <PhaseIcon $status={getPhaseStatus('complete')}>
            {getPhaseStatus('complete') === 'complete' ? (
              <Icon name='check' color='white' size='tiny' />
            ) : (
              <Icon name='check_circle' size='tiny' />
            )}
          </PhaseIcon>
          <PhaseLabel>Complete</PhaseLabel>
        </PhaseItem>
      </PhaseList>

      {/* Info Section */}
      <InfoSection>
        <Icon name='info' size='small' />
        <InfoText>
          Your Force Plates are being prepared for the firmware update. This
          process takes about 15 seconds. The plates will restart automatically
          when ready.
        </InfoText>
      </InfoSection>
    </StepContainer>
  );
};

export default BootloaderTransferStep;
